import { get, post, post_with_config } from '@/services/http';

export interface StockyConnectionStatus {
  hasKey: boolean;
  maskedKey?: string;
}

export interface StockyConnectionResult {
  connected: boolean;
}

export interface StockySupplierConflict {
  existingId: number;
  existingName: string;
  existingEmail: string;
}

export interface StockySupplierPreviewItem {
  stockyId: string;
  name: string;
  contactEmail: string;
  contactPhone: string | null;
  address: string | null;
  status: string;
  isNew: boolean;
  conflict: StockySupplierConflict | null;
}

export interface StockySupplierPreview {
  suppliers: StockySupplierPreviewItem[];
  totalCount: number;
  conflictCount: number;
  newCount: number;
}

export interface StockySupplierImportResult {
  suppliers: {
    created: number;
    updated: number;
    skipped: number;
    failed: number;
  };
  taxTypes: {
    created: number;
    skipped: number;
    failed: number;
  };
  errors: string[];
}

export interface StockyPOPreviewItem {
  stockyId: number;
  poNumber: string;
  supplierName: string | null;
  supplierMatched: boolean;
  status: string;
  orderDate: string | null;
  itemCount: number;
  matchedItems: number;
  unmatchedItems: number;
  totalCost: number;
  isDuplicate: boolean;
}

export interface StockyPOPreview {
  purchaseOrders: StockyPOPreviewItem[];
  summary: {
    totalPOs: number;
    totalItems: number;
    matchedItems: number;
    unmatchedItems: number;
    matchRate: number;
  };
}

export interface StockyPOImportJob {
  jobId: string;
  status: string;
}

export interface StockyPOJobStatus {
  status: 'pending' | 'processing' | 'completed' | 'error';
  phase?: string;
  progress?: number;
  results?: {
    created: number;
    skipped: number;
    failed: number;
    totalItems: number;
    matchedItems: number;
    unmatchedItems: number;
    errors: string[];
  };
  error?: string;
}

export interface StockyVariantSupplierPreviewItem {
  name: string;
  matched: boolean;
  assistySupplierId: number | null;
  variantCount: number;
}

export interface StockyVariantAssignmentPreview {
  suppliers: StockyVariantSupplierPreviewItem[];
  summary: {
    totalRows: number;
    totalAssignments: number;
    suppliersFound: number;
    suppliersNotFound: number;
    alreadyLinked: number;
    linkedToOther: number;
    toCreate: number;
  };
}

export interface StockyVariantAssignmentResult {
  created: number;
  skipped: number;
  reassigned: number;
  failed: number;
  errors: Array<{ supplier: string; error: string }>;
}

function extractErrorMessage(error: any, fallback: string): string {
  // error.response.data may be a JSON string (due to transformResponse) or an object
  const data = error?.response?.data;
  if (data) {
    if (typeof data === 'string') {
      try {
        const parsed = JSON.parse(data);
        if (parsed.error) return parsed.error;
      } catch {
        // not JSON
      }
    } else if (data.error) {
      return data.error;
    }
  }
  return fallback;
}

export class StockyMigrationModel {

  public static async getConnection(): Promise<StockyConnectionStatus> {
    const response = await get<StockyConnectionStatus>('/migration/stocky/connection');
    return response;
  }

  public static async connect(stockyApiKey: string): Promise<StockyConnectionResult> {
    try {
      const response = await post<StockyConnectionResult>('/migration/stocky/connect', { stockyApiKey });
      return response;
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Failed to connect to Stocky. Please check your API key.'));
    }
  }

  public static async previewSuppliers(): Promise<StockySupplierPreview> {
    try {
      const response = await get<StockySupplierPreview>('/migration/stocky/preview/suppliers');
      return response;
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Failed to load supplier preview.'));
    }
  }

  public static async importSuppliers(
    supplierConflictResolutions: Record<number, string>
  ): Promise<StockySupplierImportResult> {
    try {
      const response = await post<StockySupplierImportResult>('/migration/stocky/import/suppliers', {
        supplierConflictResolutions,
      });
      return response;
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Failed to import suppliers.'));
    }
  }

  public static async previewPurchaseOrders(statusFilters: string[] = []): Promise<StockyPOPreview> {
    try {
      const params = statusFilters.length > 0 ? `?statusFilters=${statusFilters.join(',')}` : '';
      const response = await get<StockyPOPreview>(`/migration/stocky/preview/purchase-orders${params}`);
      return response;
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Failed to load purchase order preview.'));
    }
  }

  public static async importPurchaseOrders(statusFilters: string[] = []): Promise<StockyPOImportJob> {
    try {
      const response = await post<StockyPOImportJob>('/migration/stocky/import/purchase-orders', { statusFilters });
      return response;
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Failed to start purchase order import.'));
    }
  }

  public static async getImportStatus(jobId: string): Promise<StockyPOJobStatus> {
    const response = await get<StockyPOJobStatus>(`/migration/stocky/import/status/${jobId}`);
    return response;
  }

  public static async previewVariantAssignments(file: File): Promise<StockyVariantAssignmentPreview> {
    try {
      const formData = new FormData();
      formData.append('csvFile', file, file.name);
      const response = await post_with_config<StockyVariantAssignmentPreview>(
        '/migration/stocky/preview/variant-assignments',
        formData
      );
      return response;
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Failed to preview variant assignments.'));
    }
  }

  public static async importVariantAssignments(
    file: File,
    options: { overrideExisting?: boolean } = {}
  ): Promise<StockyVariantAssignmentResult> {
    try {
      const formData = new FormData();
      formData.append('csvFile', file, file.name);
      formData.append('overrideExisting', (options.overrideExisting ?? false).toString());
      const response = await post_with_config<StockyVariantAssignmentResult>(
        '/migration/stocky/import/variant-assignments',
        formData
      );
      return response;
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Failed to import variant assignments.'));
    }
  }
}
